// Enums for the native satisfaction-survey questionnaire builder (Phase 1).
//
// SurveyTypeEnum member names mirror the app's existing chaud/froid vocabulary
// (SessionDetailsSatisfactionTypeEnum in session-details-enums.ts, DIRECT/DELAYED) so we don't
// introduce a third synonym. The string values are internal to the app — the SDK boundary is bridged
// explicitly by SURVEY_TYPE_TO_SDK / SURVEY_TYPE_FROM_SDK (form-survey.mappers), so they don't need to
// match the backend contract.
export enum SurveyTypeEnum {
  DIRECT = "direct", // Enquête à chaud
  DELAYED = "delayed", // Enquête à froid
}

// `order_events.event_name` values the backend writes when it dispatches a satisfaction form — mirrors
// Orders' `SatisfactionFormEvent` (src/lib/enums/orders/satisfaction-forms.enum.ts). Unlike SurveyTypeEnum
// above, these values DO cross the wire: they arrive verbatim on SessionNotificationItem.eventName in the
// session's Notifications list. The native survey feature never emits the Typeform-era
// BookingEventsEnum.DIRECT_PEDAGOGICAL_REPORT / DELAYED_PEDAGOGICAL_REPORT, so anything keyed on those
// two names silently misses every real survey row. (QA #1216976309903335)
export enum SatisfactionFormEventEnum {
  HOT = "hot_satisfaction_form",
  COLD = "cold_satisfaction_form",
  RESEND = "satisfaction_form_resend",
}

// A questionnaire is an ordered list of "activités". A question is simply an activité whose type is
// a question type (cf. Figma: "UNIQUEMENT LES ACTIVITÉS DE TYPE QUESTION (LIBRE OU NOTÉE)").
export enum SurveyActivityTypeEnum {
  // Content activities
  TEXT = "text",
  DOCUMENT = "document",
  VIDEO = "video",
  EMBED = "embed", // Code embed
  // Question activities — "libre" (un-scored, opinion) …
  OPEN = "open_question", // Question libre → Question ouverte
  RATING = "rating", // Question libre → Notation 1 à 10
  MULTIPLE_CHOICE = "multiple_choice", // Question libre → Choix multiples
  // … and "notée" (graded, carries a correction key). Values are app-owned; the SDK bridge is
  // QUESTION_TYPE_TO_SDK / _FROM_SDK in form-survey.mappers.
  MULTIPLE_CHOICE_SCORED = "multiple_choice_scored", // Question notée → Choix multiples
  ASSOCIATION = "association", // Question notée → Association
  GAP_FILLING = "gap_filling", // Question notée → Texte à trou
}

// Which right-hand panel the builder shows.
export enum SurveyBuilderPanelEnum {
  INFO = "info", // "Informations générales"
  ACTIVITY = "activity", // a selected activité (question or content)
}

// "Libre" question types — un-scored opinion questions, the original three.
export const SURVEY_UNSCORED_QUESTION_TYPES: readonly SurveyActivityTypeEnum[] = [
  SurveyActivityTypeEnum.OPEN,
  SurveyActivityTypeEnum.RATING,
  SurveyActivityTypeEnum.MULTIPLE_CHOICE,
];

// "Notée" question types — carry a correction key and are auto-scored server-side into `passedRate`.
export const SURVEY_SCORED_QUESTION_TYPES: readonly SurveyActivityTypeEnum[] = [
  SurveyActivityTypeEnum.MULTIPLE_CHOICE_SCORED,
  SurveyActivityTypeEnum.ASSOCIATION,
  SurveyActivityTypeEnum.GAP_FILLING,
];

// Question types = the subset of activity types that are questions; the rest are content blocks.
export const SURVEY_QUESTION_TYPES: readonly SurveyActivityTypeEnum[] = [
  ...SURVEY_UNSCORED_QUESTION_TYPES,
  ...SURVEY_SCORED_QUESTION_TYPES,
];

export const isSurveyQuestionType = (type: SurveyActivityTypeEnum | null): boolean =>
  !!type && SURVEY_QUESTION_TYPES.includes(type);

export const isScoredQuestionType = (type: SurveyActivityTypeEnum | null): boolean =>
  !!type && SURVEY_SCORED_QUESTION_TYPES.includes(type);

/**
 * Which "notée" types the type-chooser offers. All three since order-sdk 9.15.0-beta.2, which closed the
 * respondent half: `SubmitFormAnswerDto` gained `pairs` + `gapAnswers`, the respond payload gained the
 * decoupled `pairPrompts`/`pairCandidates`/`gaps`, and the backend scores all three into `passedRate`.
 *
 * Kept as its own list rather than inlined: it's the single switch for hiding a graded type again if one
 * of them regresses, without unpicking the editors, mappers or prefill.
 */
export const SURVEY_SELECTABLE_SCORED_TYPES: readonly SurveyActivityTypeEnum[] = [
  SurveyActivityTypeEnum.MULTIPLE_CHOICE_SCORED,
  SurveyActivityTypeEnum.ASSOCIATION,
  SurveyActivityTypeEnum.GAP_FILLING,
];

// The 1-to-10 rating scale used by the "Notation 1 à 10" question type and the system question
// (PRD AC-QUEST-04.4 / AC-LEARN-02.5: fixed 1→10, no scale configuration).
export const SURVEY_RATING_MIN = 1;
export const SURVEY_RATING_MAX = 10;
